import { supabase } from '@/integrations/supabase/client';
import { format, startOfDay, endOfDay } from 'date-fns';
import * as XLSX from 'xlsx';
import { 
  fetchAllSourceNames, 
  getSourceNameById,
  type SourceNameMap 
} from '@/services/procurement/enrichmentService';
export type { SourceNameMap };

export interface ProcurementDamageRecord {
  id: string;
  inspection_date: string;
  procurement_id: string;
  product_name: string;
  ordered_quantity: number;
  price_per_unit: number;
  damage_kg: number;
  compensation_kg: number;
  net_damage_kg: number;
  is_rejected: boolean;
  source_type: string;
  source_id: string | null;
  source_name: string | null;
}

export interface DamageByProduct {
  product_name: string;
  total_gross_damage_kg: number; // damage_kg before compensation
  total_net_damage_kg: number;
  total_compensation_kg: number;
  total_ordered_kg: number;
  rejected_count: number;
  damage_value: number;
  damage_percentage: number;
  record_count: number;
}

export interface DamageBySupplier {
  supplier_name: string;
  source_type: string;
  source_id: string;
  procurement_count: number;
  total_net_damage_kg: number;
  total_compensation_kg: number;
  total_ordered_kg: number;
  rejected_count: number;
  damage_rate: number;
}

export interface DamageTrendData {
  date: string;
  net_damage_kg: number;
  compensation_kg: number;
  rejected_count: number;
}

export interface DamageSummary {
  total_net_damage_kg: number;
  total_compensation_kg: number;
  total_rejected_items: number;
  total_damage_value: number;
  total_ordered_kg: number;
  damage_rate: number;
}

export interface ProcurementDamageReportData {
  records: ProcurementDamageRecord[];
  summary: DamageSummary;
  byProduct: DamageByProduct[];
  bySupplier: DamageBySupplier[];
  trendData: DamageTrendData[];
}

// Fetch core damage data and enrich with source names
export async function getProcurementDamageReportData(
  startDate: Date,
  endDate: Date
): Promise<ProcurementDamageReportData> {
  const startStr = format(startOfDay(startDate), 'yyyy-MM-dd\'T\'HH:mm:ss');
  const endStr = format(endOfDay(endDate), 'yyyy-MM-dd\'T\'HH:mm:ss');

  // ===== QUERY 1: Item-level quality data (granular per-product) =====
  const { data: itemQualityData, error: itemError } = await supabase
    .from('procurement_item_quality')
    .select(`
      id,
      damage_kg,
      compensation_kg,
      is_rejected,
      procurement_items!inner (
        product_name,
        quantity,
        price_per_unit,
        procurement_id,
        procurements!inner (
          id,
          source_type,
          source_id,
          status,
          procurement_warehouse (
            warehouse_confirmation_date
          )
        )
      ),
      procurement_quality!inner (
        id,
        created_at
      )
    `)
    .gt('damage_kg', 0);

  if (itemError) {
    console.error('Error fetching item-level damage data:', itemError);
    throw itemError;
  }

  // Collect procurement IDs covered by item-level data to exclude from fallback
  const itemLevelProcurementIds = new Set<string>();
  (itemQualityData || []).forEach((item: any) => {
    const procId = item.procurement_items?.procurements?.id || item.procurement_items?.procurement_id;
    if (procId) itemLevelProcurementIds.add(procId);
  });

  // ===== QUERY 2: Procurement-level fallback (for procurements without item-level quality) =====
  const { data: procQualityData, error: procError } = await supabase
    .from('procurement_quality')
    .select(`
      id,
      procurement_id,
      damage_kg,
      compensation_kg,
      created_at,
      procurements!inner (
        id,
        source_type,
        source_id,
        status,
        product_name,
        procurement_warehouse (
          warehouse_confirmation_date
        ),
        procurement_items (
          id,
          product_name,
          quantity,
          price_per_unit
        )
      )
    `)
    .gt('damage_kg', 0);

  if (procError) {
    console.error('Error fetching procurement-level damage data:', procError);
    throw procError;
  }

  // Fetch source names for enrichment
  const sourceNames = await fetchSourceNames();

  // ===== Helper: extract arrival date from a procurement object =====
  const getArrivalDate = (procurement: any, fallbackDate: string | null): string | null => {
    const warehouseData = procurement?.procurement_warehouse;
    const warehouseDate = warehouseData?.[0]?.warehouse_confirmation_date || warehouseData?.warehouse_confirmation_date;
    return warehouseDate || fallbackDate || null;
  };

  // ===== Helper: check date range =====
  const isInDateRange = (dateStr: string | null): boolean => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    return d >= startOfDay(startDate) && d <= endOfDay(endDate);
  };

  // ===== Helper: check valid status =====
  const isValidStatus = (status: string | undefined): boolean => {
    return ['completed', 'approved', 'paid'].includes(status || '');
  };

  // ===== PROCESS QUERY 1: Item-level records =====
  const itemRecords: ProcurementDamageRecord[] = [];
  (itemQualityData || []).forEach((item: any) => {
    const procItem = item.procurement_items;
    const procurement = procItem?.procurements;
    const arrivalDate = getArrivalDate(procurement, item.procurement_quality?.created_at);

    if (!isInDateRange(arrivalDate)) return;
    if (!isValidStatus(procurement?.status)) return;

    const damageKg = Number(item.damage_kg) || 0;
    const compensationKg = Number(item.compensation_kg) || 0;
    if (damageKg <= compensationKg) return; // fully compensated

    const netDamageKg = damageKg - compensationKg;
    const sourceType = procurement?.source_type || 'supplier';
    const sourceId = procurement?.source_id || null;
    const resolvedName = getSourceNameById(sourceType, sourceId, sourceNames);

    itemRecords.push({
      id: item.id,
      inspection_date: arrivalDate!,
      procurement_id: procurement?.id || '',
      product_name: procItem?.product_name || 'Unknown',
      ordered_quantity: Number(procItem?.quantity) || 0,
      price_per_unit: Number(procItem?.price_per_unit) || 0,
      damage_kg: damageKg,
      compensation_kg: compensationKg,
      net_damage_kg: netDamageKg,
      is_rejected: Boolean(item.is_rejected),
      source_type: sourceType,
      source_id: sourceId,
      source_name: resolvedName || getDefaultSourceName(sourceType),
    });
  });

  // ===== PROCESS QUERY 2: Procurement-level fallback =====
  const fallbackRecords: ProcurementDamageRecord[] = [];
  (procQualityData || []).forEach((pq: any) => {
    const procId = pq.procurement_id || pq.procurements?.id;
    // Skip if already covered by item-level data
    if (itemLevelProcurementIds.has(procId)) return;

    const procurement = pq.procurements;
    const arrivalDate = getArrivalDate(procurement, pq.created_at);

    if (!isInDateRange(arrivalDate)) return;
    if (!isValidStatus(procurement?.status)) return;

    const totalDamageKg = Number(pq.damage_kg) || 0;
    const totalCompensationKg = Number(pq.compensation_kg) || 0;
    if (totalDamageKg <= totalCompensationKg) return;

    const items = procurement?.procurement_items || [];
    const sourceType = procurement?.source_type || 'supplier';
    const sourceId = procurement?.source_id || null;
    const resolvedName = getSourceNameById(sourceType, sourceId, sourceNames);
    const sourceName = resolvedName || getDefaultSourceName(sourceType);

    if (items.length === 0) {
      // No items — use procurement's product_name as single record
      fallbackRecords.push({
        id: pq.id,
        inspection_date: arrivalDate!,
        procurement_id: procId,
        product_name: procurement?.product_name || 'Unknown',
        ordered_quantity: 0,
        price_per_unit: 0,
        damage_kg: totalDamageKg,
        compensation_kg: totalCompensationKg,
        net_damage_kg: totalDamageKg - totalCompensationKg,
        is_rejected: false,
        source_type: sourceType,
        source_id: sourceId,
        source_name: sourceName,
      });
    } else {
      // Distribute damage proportionally across items by quantity
      const totalQuantity = items.reduce((sum: number, it: any) => sum + (Number(it.quantity) || 0), 0);

      items.forEach((it: any, idx: number) => {
        const qty = Number(it.quantity) || 0;
        const ratio = totalQuantity > 0 ? qty / totalQuantity : 1 / items.length;
        const itemDamage = totalDamageKg * ratio;
        const itemCompensation = totalCompensationKg * ratio;
        const itemNetDamage = Math.max(0, itemDamage - itemCompensation);

        if (itemNetDamage <= 0) return;

        fallbackRecords.push({
          id: `${pq.id}-fallback-${idx}`,
          inspection_date: arrivalDate!,
          procurement_id: procId,
          product_name: it.product_name || procurement?.product_name || 'Unknown',
          ordered_quantity: qty,
          price_per_unit: Number(it.price_per_unit) || 0,
          damage_kg: itemDamage,
          compensation_kg: itemCompensation,
          net_damage_kg: itemNetDamage,
          is_rejected: false,
          source_type: sourceType,
          source_id: sourceId,
          source_name: sourceName,
        });
      });
    }
  });

  // ===== MERGE both sources =====
  const records: ProcurementDamageRecord[] = [...itemRecords, ...fallbackRecords];

  // Calculate summary using net damage
  const summary: DamageSummary = records.reduce(
    (acc, record) => ({
      total_net_damage_kg: acc.total_net_damage_kg + record.net_damage_kg,
      total_compensation_kg: acc.total_compensation_kg + record.compensation_kg,
      total_rejected_items: acc.total_rejected_items + (record.is_rejected ? 1 : 0),
      total_damage_value: acc.total_damage_value + (record.net_damage_kg * record.price_per_unit),
      total_ordered_kg: acc.total_ordered_kg + record.ordered_quantity,
      damage_rate: 0,
    }),
    {
      total_net_damage_kg: 0,
      total_compensation_kg: 0,
      total_rejected_items: 0,
      total_damage_value: 0,
      total_ordered_kg: 0,
      damage_rate: 0,
    }
  );
  
  summary.damage_rate = summary.total_ordered_kg > 0 
    ? (summary.total_net_damage_kg / summary.total_ordered_kg) * 100 
    : 0;

  // Aggregate by product using net damage
  const productMap = new Map<string, DamageByProduct>();
  records.forEach((record) => {
    const existing = productMap.get(record.product_name) || {
      product_name: record.product_name,
      total_gross_damage_kg: 0, // damage before compensation
      total_net_damage_kg: 0,
      total_compensation_kg: 0,
      total_ordered_kg: 0,
      rejected_count: 0,
      damage_value: 0,
      damage_percentage: 0,
      record_count: 0,
    };
    
    existing.total_gross_damage_kg += record.damage_kg; // Add gross damage
    existing.total_net_damage_kg += record.net_damage_kg;
    existing.total_compensation_kg += record.compensation_kg;
    existing.total_ordered_kg += record.ordered_quantity;
    existing.rejected_count += record.is_rejected ? 1 : 0;
    existing.damage_value += record.net_damage_kg * record.price_per_unit;
    existing.record_count += 1;
    
    productMap.set(record.product_name, existing);
  });
  
  const byProduct: DamageByProduct[] = Array.from(productMap.values())
    .map((p) => ({
      ...p,
      damage_percentage: p.total_ordered_kg > 0 ? (p.total_net_damage_kg / p.total_ordered_kg) * 100 : 0,
    }))
    .sort((a, b) => b.total_net_damage_kg - a.total_net_damage_kg);

  // Aggregate by source using net damage
  // Group by source_id + source_type to handle cases where same ID exists across types
  // The supplier_name will be enriched later via enrichBySupplierWithNames()
  const supplierAggMap = new Map<string, DamageBySupplier>();
  records.forEach((record) => {
    // Use source_name as display name (will be placeholder initially, enriched later)
    // Group by unique combination of source_id and source_type
    const key = `${record.source_type}-${record.source_id || 'unknown'}`;
    
    const existing = supplierAggMap.get(key) || {
      supplier_name: record.source_name || 'Unknown',
      source_type: record.source_type,
      source_id: record.source_id || '',
      procurement_count: 0,
      total_net_damage_kg: 0,
      total_compensation_kg: 0,
      total_ordered_kg: 0,
      rejected_count: 0,
      damage_rate: 0,
    };
    
    existing.total_net_damage_kg += record.net_damage_kg;
    existing.total_compensation_kg += record.compensation_kg;
    existing.total_ordered_kg += record.ordered_quantity;
    existing.rejected_count += record.is_rejected ? 1 : 0;
    existing.procurement_count += 1;
    
    supplierAggMap.set(key, existing);
  });
  
  const bySupplier: DamageBySupplier[] = Array.from(supplierAggMap.values())
    .map((s) => ({
      ...s,
      damage_rate: s.total_ordered_kg > 0 ? (s.total_net_damage_kg / s.total_ordered_kg) * 100 : 0,
    }))
    .sort((a, b) => b.total_net_damage_kg - a.total_net_damage_kg);

  // Aggregate by date for trend using net damage
  const dateMap = new Map<string, DamageTrendData>();
  records.forEach((record) => {
    const dateKey = format(new Date(record.inspection_date), 'yyyy-MM-dd');
    
    const existing = dateMap.get(dateKey) || {
      date: dateKey,
      net_damage_kg: 0,
      compensation_kg: 0,
      rejected_count: 0,
    };
    
    existing.net_damage_kg += record.net_damage_kg;
    existing.compensation_kg += record.compensation_kg;
    existing.rejected_count += record.is_rejected ? 1 : 0;
    
    dateMap.set(dateKey, existing);
  });
  
  const trendData: DamageTrendData[] = Array.from(dateMap.values())
    .sort((a, b) => a.date.localeCompare(b.date));

  return {
    records,
    summary,
    byProduct,
    bySupplier,
    trendData,
  };
}

// Helper to get default source name based on type
function getDefaultSourceName(sourceType: string): string {
  switch (sourceType) {
    case 'supplier':
      return 'Supplier';
    case 'agent':
      return 'Agent';
    case 'farm':
      return 'Farm Purchase';
    case 'harvest':
      return 'Harvest';
    default:
      return 'Unknown';
  }
}

// Fetch source names for lazy loading (called when user views supplier tab)
// Now uses centralized enrichmentService
export async function fetchSourceNames(): Promise<SourceNameMap> {
  return fetchAllSourceNames();
}

// Enrich records with source names (called after lazy load)
export function enrichRecordsWithSourceNames(
  records: ProcurementDamageRecord[],
  sourceNames: SourceNameMap
): ProcurementDamageRecord[] {
  return records.map((record) => {
    let sourceName = record.source_name;
    
    if (record.source_id) {
      const resolvedName = getSourceNameById(record.source_type, record.source_id, sourceNames);
      
      if (resolvedName) {
        sourceName = resolvedName;
      } else {
        // Fallback for unknown sources
        switch (record.source_type) {
          case 'farm':
            sourceName = 'Unknown Farm';
            break;
          case 'supplier':
            sourceName = 'Unknown Supplier';
            break;
          case 'agent':
            sourceName = 'Unknown Agent';
            break;
          case 'harvest':
            sourceName = 'Harvest';
            break;
          default:
            sourceName = 'Unknown';
        }
      }
    }
    
    return { ...record, source_name: sourceName };
  });
}

// Enrich bySupplier data with source names
export function enrichBySupplierWithNames(
  bySupplier: DamageBySupplier[],
  sourceNames: SourceNameMap
): DamageBySupplier[] {
  return bySupplier.map((item) => {
    let supplierName = item.supplier_name;
    
    if (item.source_id) {
      const resolvedName = getSourceNameById(item.source_type, item.source_id, sourceNames);
      
      if (resolvedName) {
        supplierName = resolvedName;
      } else {
        // Fallback for unknown sources
        switch (item.source_type) {
          case 'farm':
            supplierName = 'Unknown Farm';
            break;
          case 'supplier':
            supplierName = 'Unknown Supplier';
            break;
          case 'agent':
            supplierName = 'Unknown Agent';
            break;
          default:
            supplierName = 'Unknown';
        }
      }
    }
    
    return { ...item, supplier_name: supplierName };
  });
}

export function exportDamageReportToCSV(records: ProcurementDamageRecord[], filename: string) {
  const csvData = records.map((record) => ({
    Date: format(new Date(record.inspection_date), 'yyyy-MM-dd'),
    'Procurement ID': record.procurement_id,
    Product: record.product_name,
    'Ordered (kg)': record.ordered_quantity,
    'Gross Damage (kg)': record.damage_kg,
    'Compensation (kg)': record.compensation_kg,
    'Net Damage (kg)': record.net_damage_kg,
    'Damage Value (ETB)': (record.net_damage_kg * record.price_per_unit).toFixed(2),
    Rejected: record.is_rejected ? 'Yes' : 'No',
    'Source Type': record.source_type,
    Supplier: record.source_name || 'Unknown',
  }));

  const worksheet = XLSX.utils.json_to_sheet(csvData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Damage Records');
  XLSX.writeFile(workbook, `${filename}.csv`);
}

export function exportDamageReportToExcel(
  data: ProcurementDamageReportData,
  filename: string,
  dateRange: { start: Date; end: Date }
) {
  const workbook = XLSX.utils.book_new();

  // Summary sheet
  const summaryData = [
    ['Procurement Damage Report'],
    [''],
    ['Report Period', `${format(dateRange.start, 'MMM dd, yyyy')} - ${format(dateRange.end, 'MMM dd, yyyy')}`],
    [''],
    ['Summary Metrics'],
    ['Net Damage (kg)', data.summary.total_net_damage_kg.toFixed(2)],
    ['Total Compensation (kg)', data.summary.total_compensation_kg.toFixed(2)],
    ['Total Rejected Items', data.summary.total_rejected_items],
    ['Total Damage Value (ETB)', data.summary.total_damage_value.toFixed(2)],
    ['Damage Rate (%)', data.summary.damage_rate.toFixed(2)],
  ];
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

  // By Product sheet
  const productData = data.byProduct.map((p) => ({
    Product: p.product_name,
    'Gross Damage (kg)': p.total_gross_damage_kg.toFixed(2),
    'Compensation (kg)': p.total_compensation_kg.toFixed(2),
    'Net Damage (kg)': p.total_net_damage_kg.toFixed(2),
    'Ordered (kg)': p.total_ordered_kg.toFixed(2),
    'Rejected Count': p.rejected_count,
    'Damage Value (ETB)': p.damage_value.toFixed(2),
    'Damage %': p.damage_percentage.toFixed(2),
    Records: p.record_count,
  }));
  const productSheet = XLSX.utils.json_to_sheet(productData);
  XLSX.utils.book_append_sheet(workbook, productSheet, 'By Product');

  // By Supplier sheet
  const supplierData = data.bySupplier.map((s) => ({
    Supplier: s.supplier_name,
    'Source Type': s.source_type,
    Procurements: s.procurement_count,
    'Net Damage (kg)': s.total_net_damage_kg.toFixed(2),
    'Compensation (kg)': s.total_compensation_kg.toFixed(2),
    'Rejected Count': s.rejected_count,
    'Damage Rate %': s.damage_rate.toFixed(2),
  }));
  const supplierSheet = XLSX.utils.json_to_sheet(supplierData);
  XLSX.utils.book_append_sheet(workbook, supplierSheet, 'By Supplier');

  // Detailed records sheet
  const detailedData = data.records.map((r) => ({
    Date: format(new Date(r.inspection_date), 'yyyy-MM-dd'),
    'Procurement ID': r.procurement_id,
    Product: r.product_name,
    'Ordered (kg)': r.ordered_quantity,
    'Gross Damage (kg)': r.damage_kg,
    'Compensation (kg)': r.compensation_kg,
    'Net Damage (kg)': r.net_damage_kg,
    'Damage Value (ETB)': (r.net_damage_kg * r.price_per_unit).toFixed(2),
    Rejected: r.is_rejected ? 'Yes' : 'No',
    'Source Type': r.source_type,
    Supplier: r.source_name || 'Unknown',
  }));
  const detailedSheet = XLSX.utils.json_to_sheet(detailedData);
  XLSX.utils.book_append_sheet(workbook, detailedSheet, 'Detailed Records');

  XLSX.writeFile(workbook, `${filename}.xlsx`);
}
